import React, { useCallback } from 'react';
import { AdminPageHeader } from './components/AdminPageHeader';
import { AdminStatCard } from './components/AdminStatCard';

// ============================================================================
// Types
// ============================================================================

export interface Coupon {
  id: number;
  code: string;
  subscriptionMonths: number;
  currentUses: number;
  maxUses: number | null;
  validFrom: string | null;
  validUntil: string | null;
  status: 'active' | 'inactive' | 'exhausted' | 'expired';
}

export interface AdminCouponsPageProps {
  coupons: Coupon[];
  totalCoupons: number;
  activeCoupons: number;
  totalUses: number;

  // URLs
  dashboardPath: string;
  couponsPath: string;
  newCouponPath: string;
  couponPath: (id: number) => string;
  editCouponPath: (id: number) => string;
  deleteCouponPath: (id: number) => string;

  // CSRF token
  csrfToken: string;

  // Labels
  labels: {
    title: string;
    subtitle: string;
    backLabel: string;
    newCouponLabel: string;
    // Stats
    totalCouponsLabel: string;
    activeCouponsLabel: string;
    totalUsesLabel: string;
    // Table
    codeColumn: string;
    subscriptionColumn: string;
    usesColumn: string;
    validFromColumn: string;
    validUntilColumn: string;
    statusColumn: string;
    actionsColumn: string;
    // Status badges
    activeStatus: string;
    inactiveStatus: string;
    exhaustedStatus: string;
    expiredStatus: string;
    // Misc
    monthLabel: string;
    monthsLabel: string;
    unlimited: string;
    notApplicable: string;
    noCouponsFound: string;
    createOneLink: string;
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

function formatDate(dateString: string | null): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getStatusBadge(
  status: Coupon['status'],
  labels: AdminCouponsPageProps['labels']
): { label: string; className: string } {
  switch (status) {
    case 'active':
      return { label: labels.activeStatus, className: 'bg-success' };
    case 'inactive':
      return { label: labels.inactiveStatus, className: 'bg-secondary' };
    case 'exhausted':
      return { label: labels.exhaustedStatus, className: 'bg-warning' };
    case 'expired':
      return { label: labels.expiredStatus, className: 'bg-danger' };
    default:
      return { label: status, className: 'bg-secondary' };
  }
}

// ============================================================================
// Main Component
// ============================================================================

export function AdminCouponsPage({
  coupons,
  totalCoupons,
  activeCoupons,
  totalUses,
  dashboardPath,
  couponsPath,
  newCouponPath,
  couponPath,
  editCouponPath,
  deleteCouponPath,
  csrfToken,
  labels,
}: AdminCouponsPageProps) {
  const handleDelete = useCallback(
    (id: number) => {
      if (!confirm('Are you sure?')) return;

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = deleteCouponPath(id);

      const methodInput = document.createElement('input');
      methodInput.type = 'hidden';
      methodInput.name = '_method';
      methodInput.value = 'DELETE';
      form.appendChild(methodInput);

      const csrfInput = document.createElement('input');
      csrfInput.type = 'hidden';
      csrfInput.name = 'authenticity_token';
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);

      document.body.appendChild(form);
      form.submit();
    },
    [deleteCouponPath, csrfToken]
  );

  const stats = [
    {
      title: labels.totalCouponsLabel,
      value: totalCoupons,
      icon: 'gift',
      iconColor: 'primary',
    },
    {
      title: labels.activeCouponsLabel,
      value: activeCoupons,
      icon: 'check-circle',
      iconColor: 'success',
    },
    {
      title: labels.totalUsesLabel,
      value: totalUses,
      icon: 'people',
      iconColor: 'info',
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title={labels.title}
        subtitle={labels.subtitle}
        icon="gift"
        backLink={{ href: dashboardPath, label: labels.backLabel }}
        actions={
          <a href={newCouponPath} className="btn btn-primary">
            <i className="bi bi-plus-circle me-1" />
            {labels.newCouponLabel}
          </a>
        }
      />

      {/* Stats Cards */}
      <div className="row g-3 mb-4">
        {stats.map((stat, index) => (
          <div key={index} className="col-md-6 col-xl-3">
            <AdminStatCard {...stat} />
          </div>
        ))}
      </div>

      {/* Coupons Table */}
      <div className="card shadow-sm">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>{labels.codeColumn}</th>
                  <th>{labels.subscriptionColumn}</th>
                  <th>{labels.usesColumn}</th>
                  <th>{labels.validFromColumn}</th>
                  <th>{labels.validUntilColumn}</th>
                  <th>{labels.statusColumn}</th>
                  <th>{labels.actionsColumn}</th>
                </tr>
              </thead>
              <tbody>
                {coupons.length > 0 ? (
                  coupons.map((coupon) => {
                    const statusBadge = getStatusBadge(coupon.status, labels);
                    const monthLabel =
                      coupon.subscriptionMonths === 1
                        ? labels.monthLabel
                        : labels.monthsLabel;

                    return (
                      <tr key={coupon.id}>
                        <td>
                          <strong>{coupon.code}</strong>
                        </td>
                        <td>
                          {coupon.subscriptionMonths} {monthLabel}
                        </td>
                        <td>
                          {coupon.currentUses}
                          {' / '}
                          {coupon.maxUses !== null ? coupon.maxUses : labels.unlimited}
                        </td>
                        <td>
                          {coupon.validFrom
                            ? formatDate(coupon.validFrom)
                            : labels.notApplicable}
                        </td>
                        <td>
                          {coupon.validUntil
                            ? formatDate(coupon.validUntil)
                            : labels.notApplicable}
                        </td>
                        <td>
                          <span className={`badge ${statusBadge.className}`}>
                            {statusBadge.label}
                          </span>
                        </td>
                        <td>
                          <a
                            href={couponPath(coupon.id)}
                            className="btn btn-sm btn-outline-primary me-1"
                          >
                            <i className="bi bi-eye" />
                          </a>
                          <a
                            href={editCouponPath(coupon.id)}
                            className="btn btn-sm btn-outline-secondary me-1"
                          >
                            <i className="bi bi-pencil" />
                          </a>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(coupon.id)}
                          >
                            <i className="bi bi-trash" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-4">
                      {labels.noCouponsFound}{' '}
                      <a href={newCouponPath} className="text-primary">
                        {labels.createOneLink}
                      </a>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminCouponsPage;
